import {
  COMBINE_BROADCAST_R,
  COMBINE_EXTRACT_ALPHA,
  COMPOSE_SOLID,
  COMPOSE_STRAIGHT,
  FILTER_AREA,
  FILTER_NEAREST,
  GpuContext,
  GpuImage,
  combine,
  compose,
  computeBounds,
  encodeImage,
  kmeansPalette,
  quantizePalette,
  readTexture,
  resample,
  resampleTo,
  type Rect,
} from '@design-ai/gpu-core';
import type { PaletteSettings } from './settings.js';

export interface ExportOptions {
  /** Crop away fully transparent margin. */
  trim?: boolean;
  /** Alpha below this is treated as empty when trimming. */
  trimThreshold?: number;
  /** Margin kept around the trimmed artwork, in pixels. */
  trimPadding?: number;
  /** Longest edge of the exported file; the artwork is never upscaled here. */
  maxEdge?: number;
  /** Palette quantisation applied before encoding. */
  palette?: PaletteSettings;
  type?: 'image/png' | 'image/webp';
  quality?: number;
  /** Flatten onto this colour instead of keeping transparency. */
  flattenTo?: [number, number, number];
}

export interface ExportResult {
  blob: Blob;
  width: number;
  height: number;
  /** Bounding box used, in source pixels; `null` when nothing was trimmed. */
  trimmed: Rect | null;
  palette: Array<[number, number, number]> | null;
}

/**
 * Produces the print-ready file.
 *
 * Trimming is not cosmetic: print services scale the uploaded artwork into the
 * product's print area, so a transparent margin silently shrinks the design and
 * shifts it off centre.
 */
export async function exportArtwork(
  ctx: GpuContext,
  image: GpuImage,
  options: ExportOptions = {},
): Promise<ExportResult> {
  const prepared = await prepareForExport(ctx, image, options);
  let current = prepared.image;
  let owned = prepared.owned;
  const trimmed = prepared.trimmed;

  let palette: Array<[number, number, number]> | null = null;
  if (options.palette && options.palette.colors > 0) {
    palette = await extractPalette(ctx, current, options.palette.colors);
    if (palette.length > 0) {
      const quantized = ctx.image(current.width, current.height, current.format, 'export-quantized');
      quantizePalette(ctx, current, quantized, palette, {
        dither: options.palette.dither,
        linear: options.palette.linear,
      });
      if (owned) current.release();
      current = quantized;
      owned = true;
    }
  }

  const flat = ctx.image(current.width, current.height, 'rgba8unorm', 'export-flat');
  compose(ctx, current, flat, {
    mode: options.flattenTo ? COMPOSE_SOLID : COMPOSE_STRAIGHT,
    background: options.flattenTo ?? [1, 1, 1],
    premultiplied: false,
  });

  const blob = await encodeImage(ctx, flat, {
    type: options.type ?? 'image/png',
    quality: options.quality,
  });
  const width = flat.width;
  const height = flat.height;

  flat.release();
  if (owned) current.release();

  return { blob, width, height, trimmed, palette };
}

/**
 * Samples the artwork and clusters it into `colors` inks.
 *
 * Sampling at 128px keeps k-means to a few thousand points, which is plenty:
 * POD artwork has large flat areas, so the distinct inks are heavily
 * over-represented and converge immediately.
 */
export async function extractPalette(
  ctx: GpuContext,
  image: GpuImage,
  colors: number,
): Promise<Array<[number, number, number]>> {
  const edge = 128;
  const scale = Math.min(1, edge / Math.max(image.width, image.height));
  const width = Math.max(8, Math.round(image.width * scale));
  const height = Math.max(8, Math.round(image.height * scale));

  const scaled = resampleTo(ctx, image, width, height, {
    filter: FILTER_AREA,
    premultiplied: true,
  });
  const small = ctx.image(width, height, 'rgba8unorm', 'palette-probe');
  compose(ctx, scaled, small, { mode: COMPOSE_STRAIGHT });
  const bytes = await readTexture(ctx, small);
  scaled.release();
  small.release();

  return kmeansPalette(bytes, colors, { alphaThreshold: 0.5 }) as Array<[number, number, number]>;
}

interface PreparedExport {
  image: GpuImage;
  /** True when `image` is a temporary the caller must release. */
  owned: boolean;
  trimmed: Rect | null;
}

/**
 * Trim and size-limit, shared by every export path.
 *
 * Factored out so the redesign handoff derives its colour and its mask from the
 * *same* prepared texture - a mask cropped differently from its image is worse
 * than no mask at all.
 */
async function prepareForExport(
  ctx: GpuContext,
  image: GpuImage,
  options: { trim?: boolean; trimThreshold?: number; trimPadding?: number; maxEdge?: number },
): Promise<PreparedExport> {
  let current = image;
  let owned = false;
  let trimmed: Rect | null = null;

  if (options.trim !== false) {
    const bounds = await computeBounds(ctx, image, {
      threshold: options.trimThreshold ?? 0.02,
      channel: 3,
      padding: options.trimPadding ?? 0,
    });
    if (bounds && (bounds.width !== image.width || bounds.height !== image.height)) {
      const cropped = ctx.image(bounds.width, bounds.height, image.format, 'export-crop');
      resample(ctx, image, cropped, { filter: FILTER_NEAREST, srcRect: bounds });
      current = cropped;
      owned = true;
      trimmed = bounds;
    }
  }

  if (options.maxEdge && Math.max(current.width, current.height) > options.maxEdge) {
    const scale = options.maxEdge / Math.max(current.width, current.height);
    const resized = resampleTo(
      ctx,
      current,
      Math.max(1, Math.round(current.width * scale)),
      Math.max(1, Math.round(current.height * scale)),
      { filter: FILTER_AREA, premultiplied: true },
    );
    if (owned) current.release();
    current = resized;
    owned = true;
  }

  return { image: current, owned, trimmed };
}

export interface RedesignAssets {
  /** The cut-out as an RGBA PNG, transparency intact. */
  image: Blob;
  /** Alpha as an opaque greyscale PNG: white where the artwork is. */
  mask: Blob;
  width: number;
  height: number;
  /** Bounding box used, in source pixels, so a result can be placed back. */
  trimmed: Rect | null;
}

/**
 * Packages the cut-out for a redesign backend.
 *
 * The mask ships as its own image rather than as an alpha channel because that
 * is what consumers want: ComfyUI's `LoadImageMask` and every inpainting
 * endpoint take a separate greyscale image. Both come from one prepared texture
 * so they always agree pixel for pixel.
 *
 * Note there is no palette step and no upscaling here - a generative model
 * resamples whatever it is given, so upscaling first mostly buys transfer size.
 */
export async function exportForRedesign(
  ctx: GpuContext,
  image: GpuImage,
  options: { trim?: boolean; trimPadding?: number; maxEdge?: number } = {},
): Promise<RedesignAssets> {
  const prepared = await prepareForExport(ctx, image, options);
  const source = prepared.image;

  const rgba = ctx.image(source.width, source.height, 'rgba8unorm', 'redesign-rgba');
  compose(ctx, source, rgba, { mode: COMPOSE_STRAIGHT, premultiplied: false });

  const alpha = ctx.image(source.width, source.height, 'r32float', 'redesign-alpha');
  combine(ctx, source, source, alpha, COMBINE_EXTRACT_ALPHA);
  const maskRgba = ctx.image(source.width, source.height, 'rgba8unorm', 'redesign-mask');
  combine(ctx, alpha, alpha, maskRgba, COMBINE_BROADCAST_R);

  const [imageBlob, maskBlob] = await Promise.all([
    encodeImage(ctx, rgba, { type: 'image/png' }),
    encodeImage(ctx, maskRgba, { type: 'image/png' }),
  ]);

  const result: RedesignAssets = {
    image: imageBlob,
    mask: maskBlob,
    width: source.width,
    height: source.height,
    trimmed: prepared.trimmed,
  };

  rgba.release();
  alpha.release();
  maskRgba.release();
  if (prepared.owned) source.release();
  return result;
}
